import React, { useEffect, useState, useRef } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { ChevronDown, ChevronUp, Check, Maximize2, Minimize2, Info, X, Search } from 'lucide-react';
import { InteriorOption } from '../../types/interiorConfig';
import { useSelections } from './SelectionsContext';
import { useTheme } from '../styles/ThemeContext';
import { ColorSwatchPopover } from './ColorSwatchPopover';
import { useComparison } from './ComparisonContext';
import { Split } from 'lucide-react';
function MarqueeName({
  name,
  isDark
}: {
  name: string;
  isDark: boolean;
}) {
  const containerRef = useRef<HTMLDivElement>(null);
  const textRef = useRef<HTMLSpanElement>(null);
  const [overflow, setOverflow] = useState(0);
  const [isHovered, setIsHovered] = useState(false);
  useEffect(() => {
    if (containerRef.current && textRef.current) {
      const diff = textRef.current.scrollWidth - containerRef.current.clientWidth;
      setOverflow(diff > 2 ? diff + 8 : 0);
    }
  }, [name]);
  return <div ref={containerRef} className="overflow-hidden px-1" onMouseEnter={() => setIsHovered(true)} onMouseLeave={() => setIsHovered(false)} data-id="element-1374">
      <span ref={textRef} className={`block whitespace-nowrap text-[9px] font-bold text-center uppercase tracking-wider ${isDark ? 'text-white/90' : 'text-gray-800'}`} style={{
      transform: isHovered && overflow > 0 ? `translateX(-${overflow}px)` : 'translateX(0)',
      transition: isHovered && overflow > 0 ? `transform ${Math.max(1, overflow / 40)}s ease-in-out` : 'transform 0.3s ease-out'
    }} title={name} data-id="element-1375">
        {name}
      </span>
    </div>;
}
const MANAGED_ITEMS_SET = new Set(['Refrigerator', 'Range Oven', 'Dishwasher', 'Microwave', 'Countertop']);
interface OptionsGalleryProps {
  options: InteriorOption[];
  onSelect: (optionId: string) => void;
  onClose: () => void;
  category?: string;
  itemName?: string;
  docked?: boolean;
}
const getOptionPrice = (optionId: string): number => {
  const baseId = optionId.replace(/^lots-/, '').replace(/-[2-5]$/, '');
  const prices: Record<string, number> = {
    standard: 0,
    'level-1': 1500,
    'level-2': 2500,
    'level-3': 3500,
    'level-4': 4500,
    'black-electric': 2000,
    'black-gas': 2200,
    'fridge-standard': 0,
    'fridge-french-door': 1800,
    'fridge-side-by-side': 1200,
    'fridge-counter-depth': 2400,
    'fridge-smart': 3500,
    'fridge-panel-ready': 2800,
    'dw-standard': 0,
    'dw-quiet': 450,
    'dw-third-rack': 650,
    'dw-panel-ready': 850,
    'dw-smart': 1200,
    'mw-standard': 0,
    'mw-over-range': 350,
    'mw-drawer': 1200,
    'mw-built-in': 800,
    'ct-laminate': 0,
    'ct-granite': 4500,
    'ct-quartz': 5500,
    'ct-marble': 8500,
    'ct-butcher-block': 3200,
    // Flooring
    'fl-hardwood-oak': 0,
    'fl-hardwood-walnut': 1800,
    'fl-engineered-hickory': 1200,
    'fl-lvp-standard': 600,
    'fl-porcelain-tile': 2200,
    'fl-ceramic-tile': 900,
    'fl-natural-stone': 4500,
    'fl-herringbone': 3800,
    // Backsplash
    'bs-subway-white': 0,
    'bs-subway-gray': 200,
    'bs-herringbone': 800,
    'bs-hexagon': 650,
    'bs-marble-slab': 3500,
    'bs-glass-mosaic': 1200,
    'bs-zellige': 1800,
    'bs-penny-round': 950,
    'bs-natural-stone': 2800,
    'fridges-basic-28': 0,
    'fridges-standard-30': 0,
    'fridges-economy-24': 0,
    'fridges-stainless-30': 800,
    'fridges-energy-star': 950,
    'fridges-top-freezer': 1100,
    'fridges-bottom-freezer': 1300,
    'fridges-compact-plus': 1450,
    'fridges-french-33': 1800,
    'fridges-side-by-side-36': 2100,
    'fridges-counter-33': 2400,
    'fridges-glass-door': 2700,
    'fridges-column': 2950,
    'fridges-smart-pro': 3200,
    'fridges-panel-36': 3600,
    'fridges-builtin-42': 4100,
    'fridges-pro-48': 4500,
    'fridges-chef': 4900,
    'fridges-subzero-48': 5500,
    'fridges-thermador': 6200,
    'fridges-viking': 7000,
    'fridges-lacornue': 8500
  };
  return prices[optionId] ?? prices[baseId] ?? Math.floor(Math.random() * 3000) + 500;
};
const getTier = (price: number): 'Standard' | 'Level 1' | 'Level 2' | 'Level 3' | 'Level 4' => {
  if (price === 0) return 'Standard';
  if (price <= 1500) return 'Level 1';
  if (price <= 3000) return 'Level 2';
  if (price <= 5000) return 'Level 3';
  return 'Level 4';
};
const getTierColor = (tier: string, isDark: boolean) => {
  switch (tier) {
    case 'Standard':
      return isDark ? 'text-emerald-400 bg-emerald-500/20 border-emerald-500/30' : 'text-emerald-700 bg-emerald-100 border-emerald-200';
    case 'Level 1':
      return isDark ? 'text-blue-400 bg-blue-500/20 border-blue-500/30' : 'text-blue-700 bg-blue-100 border-blue-200';
    case 'Level 2':
      return isDark ? 'text-amber-400 bg-amber-500/20 border-amber-500/30' : 'text-amber-700 bg-amber-100 border-amber-200';
    case 'Level 3':
      return isDark ? 'text-purple-400 bg-purple-500/20 border-purple-500/30' : 'text-purple-700 bg-purple-100 border-purple-200';
    case 'Level 4':
      return isDark ? 'text-rose-400 bg-rose-500/20 border-rose-500/30' : 'text-rose-700 bg-rose-100 border-rose-200';
    default:
      return isDark ? 'text-gray-400 bg-gray-500/20 border-gray-500/30' : 'text-gray-700 bg-gray-100 border-gray-200';
  }
};
const TIER_ORDER: Record<string, number> = {
  Standard: 0,
  'Level 1': 1,
  'Level 2': 2,
  'Level 3': 3,
  'Level 4': 4
};
const getOptionDescription = (optionId: string): string | null => {
  const baseId = optionId.replace(/^lots-/, '').replace(/-[2-5]$/, '');
  const descriptions: Record<string, string> = {
    standard: 'Standard electric range with 4 coil burners and manual-clean oven.',
    'level-1': 'Stainless steel gas range with 5 sealed burners and self-clean.',
    'level-2': 'Professional dual-fuel range with convection oven and griddle.',
    'level-3': 'Chef series with 6 brass burners, dual convection, and warming drawer.',
    'level-4': 'Commercial-grade 48" range with 8 burners, double oven, and infrared broiler.',
    'black-electric': 'Black stainless finish, 5 smooth-top elements, steam clean.',
    'black-gas': 'Matte black gas range with 5 sealed burners and air fry mode.',
    'fridge-standard': 'Standard 30" top-mount refrigerator with manual defrost.',
    'fridge-french-door': '36" French door with dual cooling zones, ice maker, and LED lighting.',
    'fridge-side-by-side': 'Side-by-side with through-door ice and water dispenser.',
    'fridge-counter-depth': 'Counter-depth design for a built-in look, adjustable shelving.',
    'fridge-smart': 'Smart Hub with touchscreen, internal cameras, and Wi-Fi connectivity.',
    'fridge-panel-ready': 'Panel-ready for custom cabinetry integration, fully flush design.',
    'dw-standard': 'Standard 24" built-in dishwasher with 3 wash cycles.',
    'dw-quiet': 'Ultra-quiet operation at 42 dBA with stainless steel tub.',
    'dw-third-rack': 'Third rack for utensils, flexible tines, and soil sensor.',
    'dw-panel-ready': 'Panel-ready design for seamless kitchen integration.',
    'dw-smart': 'Wi-Fi enabled with remote monitoring and auto-dispense detergent.',
    'mw-standard': 'Standard 1000W countertop microwave with turntable.',
    'mw-over-range': 'Over-the-range with ventilation fan and sensor cooking.',
    'mw-drawer': 'Drawer-style microwave for ergonomic under-counter installation.',
    'mw-built-in': 'Built-in trim kit compatible, 1200W with convection mode.',
    'ct-laminate': 'Laminate surface — durable, easy to clean, budget-friendly.',
    'ct-granite': 'Natural granite — unique patterns, heat resistant, timeless appeal.',
    'ct-quartz': 'Engineered quartz — non-porous, stain resistant, consistent color.',
    'ct-marble': 'Carrara marble — elegant veining, cool surface, premium luxury.',
    'ct-butcher-block': 'Solid hardwood — warm aesthetic, ideal prep surface, refinishable.',
    // Flooring
    'fl-hardwood-oak': 'Hardwood oak — warm, natural look, requires regular maintenance.',
    'fl-hardwood-walnut': 'Walnut hardwood — rich, deep color, beautiful grain patterns.',
    'fl-engineered-hickory': 'Engineered hickory — durable, stable, and beautiful grain.',
    'fl-lvp-standard': 'LVP standard — affordable, easy to install, good durability.',
    'fl-porcelain-tile': 'Porcelain tile — water-resistant, durable, and low maintenance.',
    'fl-ceramic-tile': 'Ceramic tile — classic look, affordable, but less durable.',
    'fl-natural-stone': 'Natural stone — unique patterns, high-end look, requires sealing.',
    'fl-herringbone': 'Herringbone pattern — elegant, timeless, and sophisticated.',
    // Backsplash
    'bs-subway-white': 'Subway tile — classic white subway, timeless and versatile.',
    'bs-subway-gray': 'Subway tile — gray subway, modern and sophisticated.',
    'bs-herringbone': 'Herringbone pattern — elegant, timeless, and sophisticated.',
    'bs-hexagon': 'Hexagon pattern — modern, geometric, and stylish.',
    'bs-marble-slab': 'Marble slab — elegant veining, premium luxury, and timeless appeal.',
    'bs-glass-mosaic': 'Glass mosaic — unique patterns, modern, and eye-catching.',
    'bs-zellige': 'Zellige pattern — intricate geometric designs, traditional and beautiful.',
    'bs-penny-round': 'Penny round — classic, timeless, and elegant.',
    'bs-natural-stone': 'Natural stone — unique patterns, high-end look, requires sealing.',
    'fridges-basic-28': 'Basic 28" top-mount, manual defrost, wire shelves.',
    'fridges-standard-30': 'Standard 30" with adjustable glass shelves and crisper drawers.',
    'fridges-economy-24': 'Compact 24" apartment-size, energy efficient.',
    'fridges-stainless-30': 'Stainless steel finish, frost-free, LED interior lighting.',
    'fridges-energy-star': 'Energy Star certified, reduces energy costs by up to 15%.',
    'fridges-top-freezer': 'Top freezer with full-width deli drawer and gallon door bins.',
    'fridges-bottom-freezer': 'Bottom freezer for eye-level fridge access, pull-out drawer.',
    'fridges-compact-plus': 'Compact design with surprisingly spacious interior and ice maker.',
    'fridges-french-33': '33" French door with dual evaporators and spill-proof shelves.',
    'fridges-side-by-side-36': '36" side-by-side with external ice/water and door alarm.',
    'fridges-counter-33': 'Counter-depth 33" for a streamlined, built-in appearance.',
    'fridges-glass-door': 'Glass door display model with LED showcase lighting.',
    'fridges-column': 'Column-style single-zone for dedicated refrigeration or freezing.',
    'fridges-smart-pro': 'Smart Hub Pro with 21" touchscreen, cameras, and voice control.',
    'fridges-panel-36': '36" panel-ready for full custom cabinetry integration.',
    'fridges-builtin-42': '42" built-in with dual compressors and air purification.',
    'fridges-pro-48': '48" professional with stainless interior and magnetic door seal.',
    'fridges-chef': 'Chef Series with precise temp zones, herb garden, and wine rack.',
    'fridges-subzero-48': 'Sub-Zero 48" with NASA-inspired air purification and dual cooling.',
    'fridges-thermador': 'Thermador Freedom with full-access drawers and diamond ice maker.',
    'fridges-viking': 'Viking Professional with ProChill™ and electronic controls.',
    'fridges-lacornue': 'La Cornue handcrafted luxury — bespoke finishes, artisan quality.'
  };
  return descriptions[optionId] ?? descriptions[baseId] ?? null;
};
function TierMultiSelect({
  tiers,
  selectedTiers,
  onToggleTier,
  optionsWithTier,
  isDark
}: {
  tiers: string[];
  selectedTiers: Set<string>;
  onToggleTier: (tier: string) => void;
  optionsWithTier: Array<{
    tier: string;
  }>;
  isDark: boolean;
}) {
  const [isOpen, setIsOpen] = useState(false);
  const dropdownRef = useRef<HTMLDivElement>(null);
  useEffect(() => {
    const handleClickOutside = (e: MouseEvent) => {
      if (dropdownRef.current && !dropdownRef.current.contains(e.target as Node)) {
        setIsOpen(false);
      }
    };
    if (isOpen) {
      document.addEventListener('mousedown', handleClickOutside);
    }
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, [isOpen]);
  const allSelected = selectedTiers.size === 0;
  const selectedCount = selectedTiers.size;
  let buttonLabel = 'All Levels';
  if (selectedCount === 1) {
    buttonLabel = Array.from(selectedTiers)[0];
  } else if (selectedCount > 1) {
    buttonLabel = `${selectedCount} Levels`;
  }
  const tierOptions = tiers.filter(t => t !== 'All');
  return <div ref={dropdownRef} className="relative" data-id="element-1376">
      <button onClick={e => {
      e.stopPropagation();
      setIsOpen(!isOpen);
    }} className={`
          flex items-center gap-1.5 px-3 py-1.5 rounded-lg text-[11px] font-medium transition-all whitespace-nowrap border
          ${isDark ? 'bg-white/5 border-white/10 text-white/70 hover:bg-white/10 hover:text-white' : 'bg-gray-100 border-gray-200 text-gray-600 hover:bg-gray-200 hover:text-gray-900'}
          ${isOpen ? isDark ? 'bg-white/10 border-white/20 text-white' : 'bg-gray-200 border-gray-300 text-gray-900' : ''}
        `} data-id="element-1377">
        <span data-id="element-1378">{buttonLabel}</span>
        <ChevronDown className={`w-3 h-3 transition-transform duration-200 ${isOpen ? 'rotate-180' : ''}`} data-id="element-1379" />
      </button>
      <AnimatePresence data-id="element-1380">
        {isOpen && <motion.div initial={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} animate={{
        opacity: 1,
        y: 0,
        scale: 1
      }} exit={{
        opacity: 0,
        y: -4,
        scale: 0.97
      }} transition={{
        duration: 0.15,
        ease: [0.22, 1, 0.36, 1]
      }} className={`absolute top-full left-0 mt-1.5 z-50 min-w-[180px] rounded-xl border shadow-2xl backdrop-blur-xl overflow-hidden
              ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1381">
            <button onClick={() => onToggleTier('All')} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                ${allSelected ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1382">
              <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                ${allSelected ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1383">
                {allSelected && <Check className="w-3 h-3 text-white" data-id="element-1384" />}
              </div>
              <span className={`text-xs font-medium ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1385">
                All Levels
              </span>
              <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1386">
                {optionsWithTier.length}
              </span>
            </button>
            <div className={`mx-3 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1387" />
            {tierOptions.map(tier => {
          const count = optionsWithTier.filter(o => o.tier === tier).length;
          if (count === 0) return null;
          const isChecked = selectedTiers.has(tier);
          const tierColor = getTierColor(tier, isDark);
          return <button key={tier} onClick={() => onToggleTier(tier)} className={`w-full flex items-center gap-2.5 px-3 py-2 text-left transition-colors
                    ${isDark ? 'hover:bg-white/5' : 'hover:bg-gray-50'}
                    ${isChecked ? isDark ? 'bg-cyan-500/10' : 'bg-teal-500/5' : ''}`} data-id="element-1388">
                  <div className={`w-4 h-4 rounded flex items-center justify-center border transition-all flex-shrink-0
                    ${isChecked ? isDark ? 'bg-cyan-500 border-cyan-400' : 'bg-teal-600 border-teal-500' : isDark ? 'border-white/20 bg-white/5' : 'border-gray-300 bg-white'}`} data-id="element-1389">
                    {isChecked && <Check className="w-3 h-3 text-white" data-id="element-1390" />}
                  </div>
                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border ${tierColor}`} data-id="element-1391">
                    {tier}
                  </span>
                  <span className={`text-[10px] ml-auto ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1392">
                    {count}
                  </span>
                </button>;
        })}
          </motion.div>}
      </AnimatePresence>
    </div>;
}
export function OptionsGallery({
  options,
  onSelect,
  onClose,
  category = 'Kitchen',
  itemName = 'Option',
  docked = false
}: OptionsGalleryProps) {
  const {
    addSelection,
    selections,
    activePackage
  } = useSelections();
  const {
    isDark
  } = useTheme();
  const {
    addToComparison,
    removeFromComparison,
    isInComparison,
    comparisonItems,
    toggleComparisonMode,
    isDedicatedCompareActive
  } = useComparison();
  const scrollContainerRef = useRef<HTMLDivElement>(null);
  const [selectedTiers, setSelectedTiers] = useState<Set<string>>(new Set());
  const [isExpanded, setIsExpanded] = useState(false);
  const [activeInfoId, setActiveInfoId] = useState<string | null>(null);
  const [needsExpand, setNeedsExpand] = useState(false);
  const [isMinimized, setIsMinimized] = useState(false);
  const [collapsedHeight, setCollapsedHeight] = useState<number | null>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const [selectedColors, setSelectedColors] = useState<Record<string, string>>({});
  const handleSelectColor = (optionId: string, colorId: string) => {
    setSelectedColors(prev => ({
      ...prev,
      [optionId]: colorId
    }));
  };
  const currentSelection = selections.find(s => s.category === category && s.itemName === itemName);
  const optionsWithTier = options.map(opt => ({
    ...opt,
    price: getOptionPrice(opt.id),
    tier: getTier(getOptionPrice(opt.id))
  }));
  const sortedOptionsWithTier = [...optionsWithTier].sort((a, b) => {
    const tierDiff = (TIER_ORDER[a.tier] ?? 99) - (TIER_ORDER[b.tier] ?? 99);
    if (tierDiff !== 0) return tierDiff;
    return a.price - b.price;
  });
  const tiers = ['All', 'Standard', 'Level 1', 'Level 2', 'Level 3', 'Level 4'];
  const handleToggleTier = (tier: string) => {
    if (tier === 'All') {
      setSelectedTiers(new Set());
      return;
    }
    setSelectedTiers(prev => {
      const next = new Set(prev);
      if (next.has(tier)) {
        next.delete(tier);
      } else {
        next.add(tier);
      }
      return next;
    });
  };
  const filteredOptions = sortedOptionsWithTier.filter(opt => {
    const tierMatch = selectedTiers.size === 0 || selectedTiers.has(opt.tier);
    const searchMatch = !searchQuery.trim() || opt.name.toLowerCase().includes(searchQuery.toLowerCase());
    return tierMatch && searchMatch;
  });
  const checkOverflow = () => {
    if (scrollContainerRef.current) {
      const el = scrollContainerRef.current;
      if (isExpanded) {
        setNeedsExpand(el.scrollHeight > el.clientHeight + 10);
      } else {
        setNeedsExpand(el.scrollWidth > el.clientWidth + 10);
      }
    }
  };
  const handleSelect = (option: (typeof optionsWithTier)[0]) => {
    // If in dedicated compare mode, clicking the card toggles comparison selection
    if (isDedicatedCompareActive) {
      if (isInComparison(option.id)) {
        removeFromComparison(option.id);
      } else {
        if (comparisonItems.length < 2) {
          addToComparison({
            id: option.id,
            name: option.name,
            thumbnail: option.thumbnail,
            price: option.price,
            category: itemName,
            parentCategory: category // Pass the parent category
          });
        }
      }
      return;
    }
    addSelection({
      category,
      itemName,
      optionName: option.name,
      optionId: option.id,
      price: option.price,
      thumbnail: option.thumbnail,
      isPackageItem: false
    });
    onSelect(option.id);
  };
  const handleToggleCompare = (e: React.MouseEvent, option: (typeof optionsWithTier)[0]) => {
    e.stopPropagation();
    if (isInComparison(option.id)) {
      removeFromComparison(option.id);
    } else {
      if (comparisonItems.length < 2) {
        addToComparison({
          id: option.id,
          name: option.name,
          thumbnail: option.thumbnail,
          price: option.price,
          category: itemName,
          parentCategory: category // Pass the parent category
        });
      }
    }
  };
  useEffect(() => {
    const raf = requestAnimationFrame(() => checkOverflow());
    window.addEventListener('resize', checkOverflow);
    return () => {
      cancelAnimationFrame(raf);
      window.removeEventListener('resize', checkOverflow);
    };
  }, [filteredOptions, isExpanded]);
  const EXPANDED_3_ROW_HEIGHT = 460;
  const selectedOption = currentSelection ? filteredOptions.find(o => o.id === currentSelection.optionId) : null;
  const selectedOptionHasColors = selectedOption?.colorOptions && selectedOption.colorOptions.length > 1;
  return <div className={docked ? 'w-full pointer-events-none' : `absolute left-[260px] right-0 z-30 flex flex-col items-center justify-end pointer-events-none px-6 ${isMinimized ? 'bottom-[48px]' : 'bottom-[60px]'}`} data-id="element-1393">
      {/* Comparison Floating Action Button */}
      <AnimatePresence data-id="element-1394">
        {comparisonItems.length > 0 && !isMinimized && <motion.div initial={{
        opacity: 0,
        y: 20
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 20
      }} className="pointer-events-auto absolute -top-16 z-50 flex items-center gap-3" data-id="element-1395">
            <div className={`flex items-center gap-2 px-4 py-2 rounded-full shadow-xl backdrop-blur-xl border ${isDark ? 'bg-slate-900/90 border-white/10' : 'bg-white/90 border-gray-200'}`} data-id="element-1396">
              <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1397">
                Compare ({comparisonItems.length}/2)
              </span>
              {comparisonItems.length === 2 && <button onClick={toggleComparisonMode} className={`ml-2 px-3 py-1 rounded-full text-xs font-bold text-white shadow-lg transition-transform active:scale-95 ${isDark ? 'bg-cyan-500 hover:bg-cyan-400' : 'bg-teal-600 hover:bg-teal-500'}`} data-id="element-1398">
                  Start Comparison
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>

      {!isMinimized && selectedOptionHasColors && <div className="pointer-events-auto mb-3 flex justify-center" data-id="element-1399">
          <ColorSwatchPopover colorOptions={selectedOption!.colorOptions!} selectedColorId={selectedColors[selectedOption!.id] || null} onSelectColor={colorId => handleSelectColor(selectedOption!.id, colorId)} optionName={selectedOption!.name} data-id="element-1400" />
        </div>}

      <AnimatePresence mode="wait" data-id="element-1401">
        {isMinimized ? <motion.button key="minimized-tab" initial={{
        opacity: 0,
        y: 10
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 10
      }} transition={{
        duration: 0.2,
        ease: [0.22, 1, 0.36, 1]
      }} onClick={() => setIsMinimized(false)} className={`pointer-events-auto flex items-center gap-2 px-5 py-2.5 rounded-t-xl backdrop-blur-xl border border-b-0 shadow-lg transition-colors ${isDark ? 'bg-slate-900/90 border-white/10 hover:bg-slate-800/90 text-white/70 hover:text-white' : 'bg-white/90 border-gray-200 hover:bg-white text-gray-500 hover:text-gray-700'}`} data-id="element-1402">
            <ChevronUp className="w-4 h-4" data-id="element-1403" />
            <span className="text-[11px] font-bold uppercase tracking-wider" data-id="element-1404">
              {itemName}
            </span>
            <ChevronUp className="w-4 h-4" data-id="element-1405" />
          </motion.button> : <motion.div key="full-gallery" initial={{
        opacity: 0,
        y: 30
      }} animate={{
        opacity: 1,
        y: 0
      }} exit={{
        opacity: 0,
        y: 30
      }} transition={{
        duration: 0.25,
        ease: [0.22, 1, 0.36, 1]
      }} className={`backdrop-blur-xl border shadow-2xl pointer-events-auto overflow-hidden transition-all duration-300 flex flex-col
              ${docked ? 'w-full rounded-t-2xl border-b-0' : 'w-full max-w-5xl rounded-2xl'} 
              ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-white/40'}`} style={{
        maxHeight: isExpanded ? `${EXPANDED_3_ROW_HEIGHT}px` : collapsedHeight ? `${collapsedHeight}px` : '190px'
      }} data-id="element-1406">
            <div className={`flex items-center gap-3 px-4 py-2 border-b flex-shrink-0 ${isDark ? 'border-white/5 bg-white/5' : 'border-gray-200/50 bg-gray-50/50'}`} data-id="element-1407">
              {/* Dedicated Mode Banner */}
              {isDedicatedCompareActive ? <div className="flex-1 flex items-center justify-between" data-id="element-1408">
                  <span className={`text-xs font-bold uppercase tracking-wider ${isDark ? 'text-cyan-400' : 'text-teal-600'}`} data-id="element-1409">
                    Compare Mode: Select 2 items
                  </span>
                  <span className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1410">
                    {comparisonItems.length} / 2 Selected
                  </span>
                </div> : <>
                  <span className={`text-xs font-bold uppercase tracking-wider whitespace-nowrap ${isDark ? 'text-white/80' : 'text-gray-700'}`} data-id="element-1411">
                    {itemName}
                  </span>
                  <TierMultiSelect tiers={tiers} selectedTiers={selectedTiers} onToggleTier={handleToggleTier} optionsWithTier={optionsWithTier} isDark={isDark} data-id="element-1412" />
                  <div className="relative flex-1 max-w-[280px] min-w-[120px]" data-id="element-1413">
                    <Search className={`absolute left-2 top-1/2 -translate-y-1/2 w-3 h-3 ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1414" />
                    <input type="text" placeholder="Search..." value={searchQuery} onChange={e => setSearchQuery(e.target.value)} className={`w-full pl-7 pr-6 py-1 rounded-lg text-[11px] border focus:outline-none transition-colors ${isDark ? 'bg-white/5 border-white/10 text-white placeholder-white/30 focus:border-cyan-500/50 focus:bg-white/8' : 'bg-white/50 border-gray-200 text-gray-900 placeholder-gray-400 focus:border-teal-500/50'}`} data-id="element-1415" />
                    {searchQuery && <button onClick={() => setSearchQuery('')} className={`absolute right-1.5 top-1/2 -translate-y-1/2 p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white' : 'text-gray-400 hover:text-gray-700'}`} data-id="element-1416">
                        <X className="w-3 h-3" data-id="element-1417" />
                      </button>}
                  </div>
                  <span className={`text-[10px] whitespace-nowrap ${isDark ? 'text-white/30' : 'text-gray-400'}`} data-id="element-1418">
                    {filteredOptions.length} of {optionsWithTier.length}
                  </span>
                  <div className="flex-1" data-id="element-1419" />
                  <div className="flex items-center gap-1.5 flex-shrink-0" data-id="element-1420">
                    {(needsExpand || isExpanded) && <button onClick={() => setIsExpanded(!isExpanded)} className={`p-1.5 rounded-lg transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title={isExpanded ? 'Collapse view' : 'Expand view'} data-id="element-1421">
                        {isExpanded ? <Minimize2 className="w-4 h-4" data-id="element-1422" /> : <Maximize2 className="w-4 h-4" data-id="element-1423" />}
                      </button>}
                    <button onClick={() => setIsMinimized(true)} className={`p-1.5 rounded-full transition-colors ${isDark ? 'hover:bg-white/10 text-white/50 hover:text-white' : 'hover:bg-gray-200 text-gray-400 hover:text-gray-600'}`} title="Minimize" data-id="element-1424">
                      <ChevronDown className="w-4 h-4" data-id="element-1425" />
                    </button>
                  </div>
                </>}
            </div>

            <div className="relative flex-1 min-h-0 flex" data-id="element-1426">
              <div className="flex-1 relative min-w-0" data-id="element-1427">
                <div ref={scrollContainerRef} onClick={() => activeInfoId && setActiveInfoId(null)} className={`h-full custom-scrollbar p-4 flex gap-3 content-start ${isExpanded ? 'flex-wrap overflow-y-auto' : 'flex-nowrap overflow-hidden'}`} data-id="element-1428">
                  {filteredOptions.map(option => {
                const isSelected = currentSelection?.optionId === option.id;
                const isIncluded = option.price === 0;
                const tierColor = getTierColor(option.tier, isDark);
                const description = option.description || getOptionDescription(option.id);
                const hasInfo = !!description;
                const isInfoActive = activeInfoId === option.id;
                return <div key={option.id} className="group relative flex flex-col items-center gap-2 transition-all flex-shrink-0" data-id="element-1429">
                        <button onClick={() => handleSelect(option)} className="relative flex flex-col items-center gap-1 transition-all text-left w-full" data-id="element-1430">
                          <div className={`
                            relative rounded-xl overflow-hidden border-2 transition-all duration-300
                            ${docked ? 'w-28 h-28' : 'w-24 h-24'}
                            ${isDedicatedCompareActive ? isInComparison(option.id) ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)] scale-105' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)] scale-105' : isDark ? 'border-white/10 opacity-60 hover:opacity-100' : 'border-gray-200 opacity-60 hover:opacity-100' : isSelected ? isDark ? 'border-cyan-500 shadow-[0_0_15px_rgba(6,182,212,0.4)]' : 'border-teal-500 shadow-[0_0_15px_rgba(20,184,166,0.4)]' : isDark ? 'border-white/10 group-hover:border-white/30' : 'border-gray-200 group-hover:border-gray-300'}
                          `} data-id="element-1431">
                            {option.thumbnail ? <img src={option.thumbnail} alt={option.name} className="w-full h-full object-cover transition-transform duration-500 group-hover:scale-110" data-id="element-1432" /> : <div className={`w-full h-full flex items-center justify-center ${isDark ? 'bg-slate-800' : 'bg-gray-100'}`} data-id="element-1433">
                                <span className={`text-[10px] ${isDark ? 'text-white/20' : 'text-gray-300'}`} data-id="element-1434">
                                  No Image
                                </span>
                              </div>}
                            <div className={`absolute bottom-0 inset-x-0 py-1 overflow-hidden backdrop-blur-md ${isDark ? 'bg-black/60' : 'bg-white/80'}`} data-id="element-1435">
                              <MarqueeName name={option.name} isDark={isDark} data-id="element-1436" />
                            </div>
                            {/* Selection Indicator Logic */}
                            {isDedicatedCompareActive ? isInComparison(option.id) && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/30' : 'bg-teal-500/30'}`} data-id="element-1437">
                                    <div className={`rounded-full p-1.5 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1438">
                                      <Split className="w-4 h-4 text-white" data-id="element-1439" />
                                    </div>
                                  </div> : isSelected && <div className={`absolute inset-0 flex items-center justify-center ${isDark ? 'bg-cyan-500/20' : 'bg-teal-500/20'}`} data-id="element-1440">
                                    <div className={`rounded-full p-1 ${isDark ? 'bg-cyan-500' : 'bg-teal-500'}`} data-id="element-1441">
                                      <Check className="w-3 h-3 text-white" data-id="element-1442" />
                                    </div>
                                  </div>}
                          </div>
                          <div className="flex flex-col items-center gap-0.5 w-full px-1" data-id="element-1443">
                            <span className={`
                                text-[10px] font-bold px-2 py-0.5 rounded-full
                                ${isIncluded ? isDark ? 'text-white/30' : 'text-gray-400' : isDark ? 'bg-white/10 text-white/60' : 'bg-gray-100 text-gray-600'}
                              `} data-id="element-1444">
                              {isIncluded ? 'Included' : `+$${option.price.toLocaleString()}`}
                            </span>
                          </div>
                        </button>

                        {hasInfo && <button onClick={e => {
                    e.stopPropagation();
                    setActiveInfoId(isInfoActive ? null : option.id);
                  }} className={`absolute top-1.5 left-1.5 z-10 p-1 rounded-full backdrop-blur-md border transition-all ${isInfoActive ? isDark ? 'bg-cyan-500 border-cyan-400 text-white shadow-lg' : 'bg-teal-500 border-teal-400 text-white shadow-lg' : isDark ? 'bg-slate-900/70 border-white/10 text-white/50 hover:text-white hover:bg-slate-900/90 opacity-0 group-hover:opacity-100' : 'bg-white/70 border-gray-200 text-gray-400 hover:text-gray-700 hover:bg-white/90 opacity-0 group-hover:opacity-100'}`} style={{
                    transition: 'opacity 0.15s, background 0.15s, color 0.15s'
                  }} data-id="element-1445">
                            <Info className="w-3 h-3" data-id="element-1446" />
                          </button>}

                        <AnimatePresence data-id="element-1447">
                          {isInfoActive && <motion.div initial={{
                      opacity: 0,
                      y: 5,
                      scale: 0.95
                    }} animate={{
                      opacity: 1,
                      y: 0,
                      scale: 1
                    }} exit={{
                      opacity: 0,
                      y: 5,
                      scale: 0.95
                    }} transition={{
                      duration: 0.15
                    }} className={`absolute z-30 w-56 rounded-xl border shadow-2xl backdrop-blur-xl p-3 ${isExpanded ? 'bottom-full mb-2' : 'top-0 -translate-y-full -mt-1'} left-1/2 -translate-x-1/2 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`} onClick={e => e.stopPropagation()} data-id="element-1448">
                              <button onClick={() => setActiveInfoId(null)} className={`absolute top-2 right-2 p-0.5 rounded-full transition-colors ${isDark ? 'text-white/30 hover:text-white' : 'text-gray-300 hover:text-gray-600'}`} data-id="element-1449">
                                <X className="w-3 h-3" data-id="element-1450" />
                              </button>
                              <div className="space-y-2" data-id="element-1451">
                                <div data-id="element-1452">
                                  <h4 className={`text-xs font-bold ${isDark ? 'text-white' : 'text-gray-900'}`} data-id="element-1453">
                                    {option.name}
                                  </h4>
                                  <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded border inline-block mt-1 ${tierColor}`} data-id="element-1454">
                                    {option.tier}
                                  </span>
                                </div>
                                <p className={`text-[11px] leading-relaxed ${isDark ? 'text-white/60' : 'text-gray-600'}`} data-id="element-1455">
                                  {description}
                                </p>
                                <div className={`flex items-center justify-between pt-1 border-t ${isDark ? 'border-white/5' : 'border-gray-100'}`} data-id="element-1456">
                                  <span className={`text-[10px] font-bold ${isIncluded ? isDark ? 'text-emerald-400' : 'text-emerald-600' : isDark ? 'text-white/80' : 'text-gray-900'}`} data-id="element-1457">
                                    {isIncluded ? 'Included in base' : `+$${option.price.toLocaleString()}`}
                                  </span>
                                  {isSelected && <span className={`text-[9px] font-bold px-1.5 py-0.5 rounded-full ${isDark ? 'bg-cyan-500/20 text-cyan-400' : 'bg-teal-500/15 text-teal-600'}`} data-id="element-1458">
                                      Selected
                                    </span>}
                                </div>
                              </div>
                              <div className={`absolute left-1/2 -translate-x-1/2 w-2.5 h-2.5 rotate-45 border ${isExpanded ? `bottom-0 translate-y-1/2 border-t-0 border-l-0 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}` : `top-full -translate-y-1/2 border-b-0 border-r-0 ${isDark ? 'bg-slate-900/95 border-white/10' : 'bg-white/95 border-gray-200'}`}`} data-id="element-1459" />
                            </motion.div>}
                        </AnimatePresence>
                      </div>;
              })}
                </div>
              </div>

              {(needsExpand || isExpanded) && <button onClick={() => setIsExpanded(!isExpanded)} className={`flex-shrink-0 w-12 flex flex-col items-center justify-center gap-1.5 border-l transition-all group ${isDark ? 'border-white/5 hover:bg-white/5' : 'border-gray-200/50 hover:bg-gray-50'}`} title={isExpanded ? 'Collapse to single row' : 'Expand to gallery view'} data-id="element-1460">
                  <ChevronUp className={`w-4 h-4 transition-colors ${isExpanded ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/30 group-hover:text-white/60' : 'text-gray-300 group-hover:text-gray-500'}`} data-id="element-1461" />
                  <div className={`flex flex-col gap-1 ${isDark ? 'text-white/20 group-hover:text-white/40' : 'text-gray-300 group-hover:text-gray-400'} transition-colors`} data-id="element-1462">
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400' : 'bg-teal-500' : 'bg-current'}`} data-id="element-1463" />
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400/60' : 'bg-teal-500/60' : 'bg-current'}`} data-id="element-1464" />
                    <div className={`w-1.5 h-1.5 rounded-full ${isExpanded ? isDark ? 'bg-cyan-400/30' : 'bg-teal-500/30' : 'bg-current'}`} data-id="element-1465" />
                  </div>
                  <ChevronDown className={`w-4 h-4 transition-colors ${!isExpanded ? isDark ? 'text-cyan-400' : 'text-teal-600' : isDark ? 'text-white/30 group-hover:text-white/60' : 'text-gray-300 group-hover:text-gray-500'}`} data-id="element-1466" />
                </button>}
            </div>
          </motion.div>}
      </AnimatePresence>
    </div>;
}